import {
  CSV_FILE_TYPE,
  EMPTY_CHARACTER,
  MAX_FILE_NAME,
  MIN_FILE_NAME,
  NetworkingError,
  SHA_256_LENGTH,
} from "@pulse/shared-components";
import { flow, getRoot, Instance, types } from "mobx-state-tree";
import { getAPIClient } from "../../networking/APIClient";
import {
  downloadFile,
  insertDocumentRecord,
} from "../../utils/FileDownloadUtils";
import { RootStore } from "../../root/store/RootStore";
import { GetPresignedDownloadURLForBulkSpocTemplateRPC } from "@pulse/pulse-rpcs/lib/spoc-management/getPresignedDownloadUrlForBulkSpocTemplateRPC";
import { ValidateCSVAndUploadSpocsRPC } from "@pulse/pulse-rpcs/lib/spoc-management/validateCsvAndUploadSpocsRPC";
import { GetPresignedUploadURLForSpocsRPC } from "@pulse/pulse-rpcs/lib/spoc-management/getPresignedUploadUrlForSpocsRPC";
import {
  useGetPresignedDownloadURLForBulkSpocTemplateRPCClient,
  useGetPresignedUploadURLForSpocsRPCClientImpl,
  useValidateCSVAndUploadSpocsRPCClient,
} from "../rpcs/RPC";
import {
  BULK_SPOC_CSV_BASE_COLUMNS_COUNT,
  BULK_SPOC_TEMPLATE_CSV_FILE_NAME,
  MAX_BULK_SPOC_FILE_SIZE_IN_BYTES,
  MIN_BULK_SPOC_FILE_SIZE_IN_BYTES,
} from "../../utils/constants";
import {
  FileUploadError,
  getFileSHA,
  uploadFileToS3,
} from "../../utils/FileUploadUtils";
import { FileAttributes } from "@pulse/pulse-rpcs/lib/types/fileAttributes";
import Papa, { ParseResult } from "papaparse";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";

export enum AddMultipleSPoCsDialogState {
  FileUploaded = "FILE_UPLOADED",
  Error = "ERROR",
  ValidationErrorFound = "VALIDATION_ERROR_FOUND",
  AddMultipleSPoCs = "ADD_MULTIPLE_SPOCS",
}

export const AddMultipleSPoCsStore = types
  .model("AddMultipleSPoCsStore", {
    addMultipleSPoCsDialogState: types.optional(
      types.enumeration(Object.values(AddMultipleSPoCsDialogState)),
      AddMultipleSPoCsDialogState.AddMultipleSPoCs,
    ),
    fileName: types.optional(types.string, EMPTY_CHARACTER),
    isDownloadTemplateButtonLoading: types.optional(types.boolean, false),
    isRPCLoading: types.optional(types.boolean, false),
    rpcError: types.maybeNull(
      types.union(
        types.enumeration(Object.values(NetworkingError)),
        types.enumeration(Object.values(ValidateCSVAndUploadSpocsRPC.RPCError)),
      ),
    ),
    validationError: types.maybeNull(
      types.enumeration(Object.values(FileUploadError)),
    ),
    invalidFileDetails: types.maybeNull(types.string),
    documentId: types.maybeNull(types.string),
  })
  .views((store) => ({
    get doesStoreContainValidationError(): boolean {
      return (
        store.validationError !== null ||
        store.rpcError === ValidateCSVAndUploadSpocsRPC.RPCError.InvalidFile
      );
    },
  }))
  .actions((store) => ({
    getFileAttribute: flow(function* (file: File) {
      if (!CSV_FILE_TYPE.includes(file.type)) {
        store.addMultipleSPoCsDialogState =
          AddMultipleSPoCsDialogState.ValidationErrorFound;
        store.validationError = FileUploadError.InvalidFileFormat;
        return;
      }
      const sha256: string = yield getFileSHA(file);

      const csvText: string = yield new Response(file).text();

      const parsedData: ParseResult<{ [key: string]: string }> = Papa.parse(
        csvText,
        { header: true },
      );

      const csvBaseColumns = parsedData.meta.fields?.map((baseColumn) =>
        baseColumn.toLocaleLowerCase(),
      );
      if (csvBaseColumns === undefined) {
        store.addMultipleSPoCsDialogState =
          AddMultipleSPoCsDialogState.ValidationErrorFound;
        store.validationError = FileUploadError.InternalError;
        return;
      }

      if (csvBaseColumns.length > BULK_SPOC_CSV_BASE_COLUMNS_COUNT) {
        store.addMultipleSPoCsDialogState =
          AddMultipleSPoCsDialogState.ValidationErrorFound;
        store.validationError = FileUploadError.MaxColumnsExceeded;
        return;
      }

      try {
        if (sha256.length !== SHA_256_LENGTH) {
          store.addMultipleSPoCsDialogState =
            AddMultipleSPoCsDialogState.ValidationErrorFound;
          store.validationError = FileUploadError.InvalidFileSHA;
          return;
        } else if (
          file.name.length < MIN_FILE_NAME ||
          file.name.length > MAX_FILE_NAME
        ) {
          store.addMultipleSPoCsDialogState =
            AddMultipleSPoCsDialogState.ValidationErrorFound;
          store.validationError = FileUploadError.InvalidFileName;
          return;
        } else if (file.size > MAX_BULK_SPOC_FILE_SIZE_IN_BYTES) {
          store.addMultipleSPoCsDialogState =
            AddMultipleSPoCsDialogState.ValidationErrorFound;
          store.validationError = FileUploadError.MaxFileSizeReached;
          return;
        } else if (file.size < MIN_BULK_SPOC_FILE_SIZE_IN_BYTES) {
          store.addMultipleSPoCsDialogState =
            AddMultipleSPoCsDialogState.ValidationErrorFound;
          store.validationError = FileUploadError.MinFileSizeNotReached;
          return;
        }

        const fileAttribute = new FileAttributes(sha256, file.name, file.size);
        return fileAttribute;
      } catch (error) {
        console.error(error);
        store.addMultipleSPoCsDialogState = AddMultipleSPoCsDialogState.Error;
        store.validationError = FileUploadError.InternalError;
      }
    }),
  }))
  .actions((store) => ({
    resetStore: (): void => {
      store.fileName = EMPTY_CHARACTER;
      store.isDownloadTemplateButtonLoading = false;
      store.addMultipleSPoCsDialogState =
        AddMultipleSPoCsDialogState.AddMultipleSPoCs;
      store.addMultipleSPoCsDialogState =
        AddMultipleSPoCsDialogState.AddMultipleSPoCs;
      store.rpcError = null;
      store.validationError = null;
      store.documentId = null;
      store.invalidFileDetails = null;
      store.isRPCLoading = false;
    },
    setIsRPCLoading: (isRPCLoading: boolean): void => {
      store.isRPCLoading = isRPCLoading;
    },
    setFileName: (fileName: string): void => {
      store.fileName = fileName;
    },
    resetRPCError: (): void => {
      store.rpcError = null;
    },
    validateCSVAndUploadSPoCs: flow(function* (projectId: string) {
      if (
        store.rpcError !== null ||
        store.validationError !== null ||
        store.invalidFileDetails !== null
      ) {
        return;
      }
      const errorStore =
        getRoot<typeof RootStore>(store).networkingStore.errorStore;
      try {
        const apiClient = getAPIClient(store);
        const request = new ValidateCSVAndUploadSpocsRPC.Request(
          new LeoUUID(projectId),
          new LeoUUID(store.documentId),
        );
        const {
          response,
          error,
        }: {
          response?: ValidateCSVAndUploadSpocsRPC.Response;
          error?: ValidateCSVAndUploadSpocsRPC.Errors.Errors;
        } =
          yield useValidateCSVAndUploadSpocsRPCClient(apiClient).execute(
            request,
          );
        if (response) {
          store.addMultipleSPoCsDialogState =
            AddMultipleSPoCsDialogState.FileUploaded;
        }
        if (error) {
          switch (error.code) {
            case ValidateCSVAndUploadSpocsRPC.RPCError.InvalidProjectId:
              break;
            case ValidateCSVAndUploadSpocsRPC.RPCError.ProjectAlreadyArchived: {
              store.rpcError =
                ValidateCSVAndUploadSpocsRPC.RPCError.ProjectAlreadyArchived;
              store.addMultipleSPoCsDialogState =
                AddMultipleSPoCsDialogState.Error;
              break;
            }
            case ValidateCSVAndUploadSpocsRPC.RPCError.InvalidFile: {
              store.rpcError =
                ValidateCSVAndUploadSpocsRPC.RPCError.InvalidFile;
              if (
                error instanceof ValidateCSVAndUploadSpocsRPC.Errors.InvalidFile
              ) {
                store.invalidFileDetails = error.invalidFileDetails;
              }
              break;
            }
            case ValidateCSVAndUploadSpocsRPC.RPCError.UnknownFile: {
              store.rpcError =
                ValidateCSVAndUploadSpocsRPC.RPCError.UnknownFile;
              store.addMultipleSPoCsDialogState =
                AddMultipleSPoCsDialogState.Error;
              break;
            }
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occurred: ${e}`);
        }
      }
    }),
    uploadCSVFile: flow(function* (file: File, projectId: string) {
      const errorStore =
        getRoot<typeof RootStore>(store).networkingStore.errorStore;
      try {
        const apiClient = getAPIClient(store);

        const fileAttribute: FileAttributes | undefined =
          yield store.getFileAttribute(file);

        if (fileAttribute === undefined) {
          return;
        }
        const request = new GetPresignedUploadURLForSpocsRPC.Request(
          fileAttribute,
          new LeoUUID(projectId),
        );

        const {
          response,
          error,
        }: {
          response?: GetPresignedUploadURLForSpocsRPC.Response;
          error?: GetPresignedUploadURLForSpocsRPC.Errors.Errors;
        } =
          yield useGetPresignedUploadURLForSpocsRPCClientImpl(
            apiClient,
          ).execute(request);
        if (response) {
          try {
            yield uploadFileToS3(response.documentURL, file);
            store.documentId = yield insertDocumentRecord(
              fileAttribute,
              apiClient,
            );
          } catch (e) {
            store.addMultipleSPoCsDialogState =
              AddMultipleSPoCsDialogState.Error;
            store.validationError = FileUploadError.InternalError;
          }
        } else if (error) {
          switch (error.code) {
            case GetPresignedUploadURLForSpocsRPC.RPCError.InvalidProjectId:
            case GetPresignedUploadURLForSpocsRPC.RPCError
              .ProjectAlreadyArchived: {
              store.rpcError =
                GetPresignedUploadURLForSpocsRPC.RPCError.ProjectAlreadyArchived;
              break;
            }
          }
          store.addMultipleSPoCsDialogState = AddMultipleSPoCsDialogState.Error;
        }
      } catch (e) {
        store.addMultipleSPoCsDialogState = AddMultipleSPoCsDialogState.Error;
        if (e instanceof Error) {
          errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occurred: ${e}`);
        }
      }
    }),
    downloadBulkSPoCUploadTemplate: flow(function* () {
      try {
        store.isDownloadTemplateButtonLoading = true;
        const apiClient = getAPIClient(store);
        const request =
          new GetPresignedDownloadURLForBulkSpocTemplateRPC.Request();
        const {
          response,
          error,
        }: {
          response?: GetPresignedDownloadURLForBulkSpocTemplateRPC.Response;
          error?: GetPresignedDownloadURLForBulkSpocTemplateRPC.Errors.Errors;
        } =
          yield useGetPresignedDownloadURLForBulkSpocTemplateRPCClient(
            apiClient,
          ).execute(request);
        if (response) {
          yield downloadFile(
            response.documentURL.href,
            BULK_SPOC_TEMPLATE_CSV_FILE_NAME,
          );
        }
        if (error) {
          store.rpcError = NetworkingError.InternalError;
        }
      } catch (e) {
        if (e instanceof Error) {
          const rootStore = getRoot<typeof RootStore>(store);
          rootStore.networkingStore.errorStore.setLeoError(e);
        } else {
          console.error(`Unhandled error occurred: ${e}`);
        }
      } finally {
        store.isDownloadTemplateButtonLoading = false;
      }
    }),
  }));

export const createAddMultipleSPoCsStore = (): Instance<
  typeof AddMultipleSPoCsStore
> => {
  return AddMultipleSPoCsStore.create({});
};
